// User-facing (Spanish) wording for computed compliance data.
import {
  type Alert,
  type AuditEntry,
  type Client,
  type DeadlineStatus,
  DOCUMENT_TYPE_LABELS,
  DOSSIER_FIELD_LABELS,
  type DocumentType,
  daysUntilDeadline,
  formatDate,
  formatMoney,
  formatMonth,
  formatNumber,
  RISK_REASON_LABELS,
  UMA_CASH_LIMIT,
} from "@pcj/shared";
import type { Tone } from "../components/ui";

/** "septiembre de 2026" → "Septiembre de 2026" (only the first letter, unlike CSS capitalize). */
export const capitalizeFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Picks the singular or plural form: plural(1, "dato", "datos") → "1 dato". */
export const plural = (count: number, singular: string, pluralForm: string) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/** "octubre de 2026" → "Octubre de 2026". */
export const monthTitle = (month: string) => capitalizeFirst(formatMonth(month));

const sizeFormatter = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 });

/** 1536 → "1.5 KB"; 3_400_000 → "3.2 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${sizeFormatter.format(bytes / 1024)} KB`;
  return `${sizeFormatter.format(bytes / 1024 / 1024)} MB`;
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "company.registered": "Registró la empresa",
  "company.updated": "Actualizó los datos de la empresa",
  "company.viewed": "Consultó el expediente (Cámara)",
  "company.exported": "Descargó un respaldo",
  "client.created": "Registró un cliente",
  "client.updated": "Actualizó un cliente",
  "client.deleted": "Eliminó un cliente",
  "operation.created": "Registró una operación",
  "operation.updated": "Actualizó una operación",
  "operation.deleted": "Eliminó una operación",
  "notice.filed": "Registró un Aviso presentado",
  "zero_report.filed": "Registró un informe en cero",
  "zero_report.unfiled": "Deshizo un informe en cero",
  "alert.dismissed": "Descartó una alerta",
  "alert.restored": "Restauró una alerta",
  "invitation.created": "Invitó a una persona",
  "invitation.revoked": "Canceló una invitación",
  "invitation.accepted": "Aceptó una invitación",
  "member.deactivated": "Desactivó el acceso de una persona",
  "member.reactivated": "Reactivó el acceso de una persona",
  "document.uploaded": "Subió un documento",
  "document.viewed": "Consultó un documento",
  "document.downloaded": "Descargó un documento",
  "document.deleted": "Eliminó un documento",
  "report.downloaded": "Descargó un resumen de operaciones (PDF)",
};

const text = (value: unknown) => (typeof value === "string" && value.trim() !== "" ? value : null);

/** What an audit entry was about, when its details say it ("INE · ine.pdf", "Mes: octubre de 2026"). */
export function auditDetail(entry: Pick<AuditEntry, "action" | "details" | "entityId">): string | null {
  const d = entry.details ?? {};
  switch (entry.action) {
    case "invitation.created":
    case "invitation.revoked":
    case "invitation.accepted":
    case "member.deactivated":
    case "member.reactivated":
      return text(d.email);
    case "client.created":
      return text(d.name);
    case "operation.created": {
      if (typeof d.amount !== "number") return null;
      const date = text(d.date);
      return `${formatMoney(d.amount)} sin IVA${date ? ` · ${formatDate(date)}` : ""}`;
    }
    case "zero_report.filed":
    case "zero_report.unfiled":
      return entry.entityId ? formatMonth(entry.entityId) : null;
    case "alert.dismissed":
      return text(d.reason);
    case "document.uploaded":
    case "document.viewed":
    case "document.downloaded": {
      const type = DOCUMENT_TYPE_LABELS[d.type as DocumentType] ?? null;
      return [type, text(d.fileName), text(d.clientName)].filter(Boolean).join(" · ") || null;
    }
    case "document.deleted": {
      const snapshot = (d.snapshot ?? {}) as Record<string, unknown>;
      return text(snapshot.fileName);
    }
    case "report.downloaded":
      return text(d.month) ? `Mes: ${formatMonth(d.month as string)}` : "Resumen general";
    default:
      return null;
  }
}

export function deadlineLabel(status: DeadlineStatus | "not_required", deadline: string, today: string): string {
  const days = daysUntilDeadline(deadline, today);
  switch (status) {
    case "filed":
      return "Presentado";
    case "not_required":
      return "No requerido";
    case "overdue":
      return days === -1 ? "Venció ayer" : `Vencido hace ${-days} días`;
    case "due_soon":
      return days === 0 ? "Vence hoy" : days === 1 ? "Vence mañana" : `Vence en ${days} días`;
    case "on_time":
      return `Vence el ${formatDate(deadline)}`;
  }
}

export const DEADLINE_TONES: Record<DeadlineStatus | "not_required", Tone> = {
  filed: "green",
  overdue: "red",
  due_soon: "amber",
  on_time: "blue",
  not_required: "neutral",
};

export const ALERT_TITLES: Record<Alert["type"], string> = {
  individual_notice: "Aviso por operación individual",
  accumulated_notice: "Aviso por acumulación (6 meses)",
  cash_limit_exceeded: "Efectivo por encima del tope",
  high_risk_client: "Cliente de Riesgo alto",
  incomplete_dossier: "Expediente incompleto",
};

export function alertDetail(alert: Alert, client: Client | undefined): string {
  switch (alert.type) {
    case "individual_notice":
      return `Operación de ${formatMoney(alert.notice.amount)} (sin IVA) del ${formatDate(alert.notice.date)} alcanza el umbral de Aviso. Fecha límite: ${formatDate(alert.notice.deadline)}.`;
    case "accumulated_notice":
      return `${alert.notice.operationIds.length} operaciones en 6 meses suman ${formatMoney(alert.notice.amount)} (sin IVA) al ${formatDate(alert.notice.date)}. Fecha límite: ${formatDate(alert.notice.deadline)}.`;
    case "cash_limit_exceeded":
      return `Pago en efectivo por ${formatMoney(alert.amountWithVat)} (con IVA) del ${formatDate(alert.date)} rebasa el tope de ${formatNumber(UMA_CASH_LIMIT)} UMA del art. 32.`;
    case "high_risk_client":
      if (alert.automaticReason)
        return `${RISK_REASON_LABELS[alert.automaticReason]}: Riesgo alto automático, requiere debida diligencia reforzada.`;
      return alert.pep
        ? "Persona Políticamente Expuesta: requiere debida diligencia reforzada."
        : `${client?.name ?? "El cliente"} tiene Grado de Riesgo alto.`;
    case "incomplete_dossier":
      return `Ya alcanzó el umbral de identificación y faltan datos: ${alert.missing.map((f) => DOSSIER_FIELD_LABELS[f]).join(", ")}.`;
  }
}
